/*
 * PortalNav — admin-ui wrapper. Renders the resolved nav tree the portal built
 * at login (sessionStorage.navEntries). No nav logic here: all gating and
 * resolution happen once in the portal, not per-SPA.
 */

import { ReactComponent as IconActivity } from '@/assets/svgs/icon-activity.svg';
import { ReactComponent as IconCaretDown } from '@/assets/svgs/caret-down.svg';
import { ReactComponent as IconApprovalRequests } from '@/assets/svgs/icon-approval-requests-nav.svg';
import { ReactComponent as IconClient } from '@/assets/svgs/icon-ds-user-group.svg';
import { ReactComponent as IconDashboard } from '@/assets/svgs/icon-dashboard.svg';
import { ReactComponent as IconDataDictionary } from '@/assets/svgs/SelectionYes.svg';
import { ReactComponent as IconDataExchange } from '@/assets/svgs/icon-ds-data-element.svg';
import { ReactComponent as IconHome } from '@/assets/svgs/icon-home.svg';
import { ReactComponent as IconParticipants } from '@/assets/svgs/Participants.svg';
import { ReactComponent as IconPitStop } from '@/assets/svgs/pitstop.svg';
import { ReactComponent as IconRelationShipSetup } from '@/assets/svgs/icon-ds-relation-status.svg';
import { ReactComponent as IconServiceProvider } from '@/assets/svgs/icon-ds-service-provider.svg';
import { ReactComponent as IconUseCase } from '@/assets/svgs/icon-use-cases.svg';
import { ReactComponent as IconUserManagement } from '@/assets/svgs/User-Management.svg';

// Pitstop-surface icons — the unified nav renders both surfaces in one sidebar,
// so dual-surface users need pitstop items iconned here too. (SVGs copied from
// pitstop-ui's assets so the keys resolve in this shell as well.)
import { ReactComponent as IconShareData } from '@/assets/svgs/share-icon.svg';
import { ReactComponent as IconReceiveData } from '@/assets/svgs/received-icon.svg';
import { ReactComponent as IconSubmittals } from '@/assets/svgs/submittals-icon.svg';
import { ReactComponent as IconEclip } from '@/assets/svgs/Eclip.svg';
import { ReactComponent as IconPackage } from '@/assets/svgs/package-data-medium-outline.svg';
import { ReactComponent as IconRecordTransfer } from '@/assets/svgs/record-transfer-icon.svg';
import { ReactComponent as ApprovalIcon } from '@/assets/svgs/approvals-icon.svg';
import { ReactComponent as IconAuditTrail } from '@/assets/svgs/ActivityLog.svg';
import { ReactComponent as IconConfigure } from '@/assets/svgs/Configurations.svg';
import { ReactComponent as IconApiConfig } from '@/assets/svgs/api_config.svg';
import { ReactComponent as IconDataSetting } from '@/assets/svgs/data-setting.svg';
import { ReactComponent as IconLicenseKey } from '@/assets/svgs/license_key.svg';

import NavView from '@/components/NavView';
import { useNavigate, useLocation } from '@/lib/router-dom';
import { getDexConfig } from '@/utils/get-dex-config';
import { getEnvAndDex } from '@/utils/get-env-and-dex';

// Keys match the `icon` values the portal's NAV_CONFIG emits onto each item.
const iconMap = {
	home: IconHome,
	'use-cases': IconUseCase,
	dashboard: IconDashboard,
	'relationship-setup': IconRelationShipSetup,
	'data-exchange': IconDataExchange,
	'service-provider': IconServiceProvider,
	client: IconClient,
	'user-management': IconUserManagement,
	pitstop: IconPitStop,
	activity: IconActivity,
	participants: IconParticipants,
	'approval-requests': IconApprovalRequests,
	'data-dictionary': IconDataDictionary,
	'data-element': IconDataExchange,
	// Pitstop surface (rendered in the same unified sidebar).
	'share-data': IconShareData,
	'receive-data': IconReceiveData,
	submittals: IconSubmittals,
	eclip: IconEclip,
	package: IconPackage,
	'record-transfer': IconRecordTransfer,
	approvals: ApprovalIcon,
	'audit-trail': IconAuditTrail,
	configure: IconConfigure,
	'api-config': IconApiConfig,
	'data-setting': IconDataSetting,
	'license-key': IconLicenseKey,
	'caret-down': IconCaretDown,
};

function readNavEntries() {
	try {
		const raw = sessionStorage.getItem('navEntries');
		return raw ? JSON.parse(raw) : [];
	} catch {
		return [];
	}
}

function PortalNav() {
	const entries = readNavEntries();
	const { dex } = getEnvAndDex();
	const { LogoComponent } = getDexConfig(dex);
	const { goTo } = useNavigate();
	// Subscribe to location so the active highlight updates on client-side nav.
	useLocation();

	const base = process.env.PUBLIC_URL || '';
	const activePathname = typeof window !== 'undefined' ? window.location.pathname : '/';

	// Same-surface links (within this shell's prefix) navigate client-side to
	// avoid a full-page reload (and its flash-of-unstyled-content). Cross-surface
	// links fall through to the <a href> full reload. Modified clicks (new tab,
	// etc.) are left to the browser.
	const onItemClick = (e, path) => {
		if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
			return;
		}
		if (base && path.startsWith(base + '/')) {
			e.preventDefault();
			goTo(path.slice(base.length));
		}
	};

	const header = (
		<div className="cl-nav-view__brand" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
			<LogoComponent />
		</div>
	);

	return (
		<NavView
			entries={entries}
			activePathname={activePathname}
			header={header}
			iconMap={iconMap}
			onItemClick={onItemClick}
		/>
	);
}

export default PortalNav;
